import { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  Toggle,
  Heading,
  Box,
  Divider,
  Flex,
  Toast,
  Dropdown,
} from '@vibe/core';
import { useSaveSettings, useLoadSettings, useFetchBoards, useMondaySDK } from '../hooks/useMondaySDK';
import { AppSettings, DEFAULT_SETTINGS, ChatWidget, TwilioIntegration } from '../types/settings';
import { createConfiguration, createWidget, deleteWidget, deleteConfiguration, updateConfiguration, fetchConfigurations } from '../services/api';
import './SettingsPage.css';

/**
 * Settings page component for Monday.com app
 * Provides UI for managing app configuration and preferences
 *
 * @returns {JSX.Element} The settings page component
 */
const SettingsPage = (): JSX.Element => {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'positive' | 'negative'>('positive');
  const [mondayAccountId, setMondayAccountId] = useState<string>('');

  // Chat widget state
  const [newWidgetName, setNewWidgetName] = useState('');
  const [showWidgetForm, setShowWidgetForm] = useState(false);
  const [mappingWidgetId, setMappingWidgetId] = useState<number | null>(null);
  const [showInstructionsWidgetId, setShowInstructionsWidgetId] = useState<number | null>(null);

  // Twilio integration state
  const [showIntegrationForm, setShowIntegrationForm] = useState(false);
  const [mappingIntegrationId, setMappingIntegrationId] = useState<string | null>(null);
  const [newIntegration, setNewIntegration] = useState<Partial<TwilioIntegration>>({
    type: 'sms',
    provider: 'twilio',
    accountSid: '',
    authToken: '',
    phoneNumber: '',
    active: true,
  });
  const [newIntegrationBoardId, setNewIntegrationBoardId] = useState<string>('');
  const [newIntegrationBoardName, setNewIntegrationBoardName] = useState<string>('');

  const { sdk } = useMondaySDK();
  const saveSettings = useSaveSettings();
  const loadSettings = useLoadSettings();
  const { boards, isLoading: boardsLoading } = useFetchBoards();

  /**
   * Fetch configurations from backend and sync to frontend state
   * This function reconstructs the complete state from backend configurations
   * All widget data is embedded in the configuration response
   */
  const loadConfigurationsFromBackend = useCallback(async () => {
    try {
      // Fetch all configurations from backend
      const configurationsResponse = await fetchConfigurations();

      if (!configurationsResponse.success) {
        throw new Error(configurationsResponse.error || 'Failed to fetch configurations');
      }

      const configurations = configurationsResponse.data || [];
      console.log('Fetched configurations:', configurations);
      console.log('Available boards:', boards);

      // Build chat widgets from livechat configurations
      const chatWidgetsMap = new Map<number, ChatWidget>();

      configurations.forEach((config) => {
        if (config.source_type === 'livechat' && config.widget) {
          const widgetId = config.widget.id;
          const board = boards.find((b) => b.id === config.board_id);
          console.log(`Widget ${widgetId}: boardId=${config.board_id}, found board=`, board);

          // Create or update widget with configuration data
          chatWidgetsMap.set(widgetId, {
            id: config.widget.id,
            name: config.widget.name,
            enabled: config.widget.enabled,
            createdAt: config.created_at,
            accountId: mondayAccountId,
            boardId: config.board_id,
            boardName: board?.name,
            configurationId: config.id,
          });
        }
      });

      // Build Twilio integrations from sms/whatsapp configurations
      const twilioIntegrations: TwilioIntegration[] = [];

      configurations.forEach((config) => {
        if (config.source_type === 'sms-twilio' || config.source_type === 'whatsapp-twilio') {
          const integrationType = config.source_type === 'sms-twilio' ? 'sms' : 'whatsapp';
          const board = boards.find((b) => b.id === config.board_id);
          console.log(`Integration ${config.id}: boardId=${config.board_id}, found board=`, board);

          // Note: We cannot retrieve accountSid and authToken from backend for security reasons
          // These are stored encrypted and not returned in GET requests
          // Users will need to re-enter credentials if they're not in local state
          twilioIntegrations.push({
            id: config.id, // Use configuration ID as integration ID
            type: integrationType,
            provider: 'twilio',
            phoneNumber: config.phone_number || config.source_main,
            accountSid: config.tokens?.account_sid || '',
            authToken: config.tokens?.auth_token || '',
            active: config.active,
            createdAt: config.created_at,
            accountId: mondayAccountId,
            boardId: config.board_id,
            boardName: board?.name,
            configurationId: config.id,
          });
        }
      });

      // Update state with synced data - use functional update to avoid stale closure
      setSettings((currentSettings) => {
        const syncedSettings: AppSettings = {
          ...currentSettings,
          chatWidgets: Array.from(chatWidgetsMap.values()),
          twilioIntegrations,
        };

        // Save synced state to Monday storage
        saveSettings('app_settings', syncedSettings);

        return syncedSettings;
      });

      showNotification(
        `Synced ${chatWidgetsMap.size} widgets and ${twilioIntegrations.length} integrations`,
        'positive'
      );
    } catch (error) {
      console.error('Error loading configurations:', error);
      showNotification(
        `Error syncing data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'negative'
      );
    }
  }, [boards, mondayAccountId, saveSettings]);

  /**
   * Load settings and Monday context on component mount
   */
  useEffect(() => {
    const loadInitialSettings = async () => {
      try {
        // Get Monday.com context to extract account ID
        sdk.listen('context', (res: any) => {
          console.log('1* Monday context:', res, res.data?.account?.id);
          if (res.data?.account?.id) {
            setMondayAccountId(res.data.account.id);
          }
        });

        const savedSettings = await loadSettings<AppSettings>('app_settings');
        if (savedSettings) {
          setSettings(savedSettings);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        showNotification('Failed to load settings', 'negative');
      }
    };

    loadInitialSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sync from backend when boards are loaded
   * This ensures board names are populated correctly
   */
  useEffect(() => {
    if (!boardsLoading && boards.length > 0) {
      loadConfigurationsFromBackend();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardsLoading, boards.length]);

  /**
   * Display a notification toast
   *
   * @param {string} message - Message to display
   * @param {'positive' | 'negative'} type - Toast type
   */
  const showNotification = (message: string, type: 'positive' | 'negative') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };


  /**
   * Create a new chat widget via backend API
   */
  const handleCreateWidget = async () => {
    if (!newWidgetName.trim()) {
      showNotification('Widget name is required', 'negative');
      return;
    }

    if (!mondayAccountId) {
      showNotification('Monday account ID not available', 'negative');
      return;
    }

    try {
      // Create widget on backend
      const response = await createWidget({
        name: newWidgetName,
      });

      if (response.success && response.data) {
        // Add widget to local state
        const newWidget: ChatWidget = {
          id: response.data.id,
          name: response.data.name,
          enabled: response.data.enabled,
          createdAt: response.data.createdAt,
          accountId: mondayAccountId,
        };

        const updatedSettings = {
          ...settings,
          chatWidgets: [...settings.chatWidgets, newWidget],
        };
        setSettings(updatedSettings);

        // Save to Monday storage
        await saveSettings('app_settings', updatedSettings);

        setNewWidgetName('');
        setShowWidgetForm(false);
        showNotification('Widget created successfully', 'positive');
      } else {
        showNotification(`Failed to create widget: ${response.error}`, 'negative');
      }
    } catch (error) {
      console.error('Error creating widget:', error);
      showNotification('Failed to create widget', 'negative');
    }
  };

  /**
   * Delete a chat widget via backend API
   *
   * @param {number} widgetId - ID of the widget to delete
   */
  const handleDeleteWidget = async (widgetId: number) => {
    if (!mondayAccountId) {
      showNotification('Monday account ID not available', 'negative');
      return;
    }

    try {
      // Delete widget on backend
      const response = await deleteWidget(widgetId);

      if (response.success) {
        // Remove from local state
        const updatedSettings = {
          ...settings,
          chatWidgets: settings.chatWidgets.filter((w) => w.id !== widgetId),
        };
        setSettings(updatedSettings);

        // Save to Monday storage
        await saveSettings('app_settings', updatedSettings);

        showNotification('Widget deleted successfully', 'positive');
      } else {
        showNotification(`Failed to delete widget: ${response.error}`, 'negative');
      }
    } catch (error) {
      console.error('Error deleting widget:', error);
      showNotification('Failed to delete widget', 'negative');
    }
  };

  /**
   * Toggle widget enabled state
   *
   * @param {number} widgetId - ID of the widget to toggle
   */
  const handleToggleWidget = (widgetId: number) => {
    setSettings({
      ...settings,
      chatWidgets: settings.chatWidgets.map((w) =>
        w.id === widgetId ? { ...w, enabled: !w.enabled } : w
      ),
    });
  };

  /**
   * Update widget board mapping and create configuration on backend
   *
   * @param {number} widgetId - ID of the widget to update
   * @param {string} boardId - Monday.com board ID
   * @param {string} boardName - Monday.com board name
   */
  const handleMapWidget = async (widgetId: number, boardId: string, boardName: string) => {
    const widget = settings.chatWidgets.find((w) => w.id === widgetId);
    if (!widget) return;

    if (!mondayAccountId) {
      showNotification('Monday account ID not available', 'negative');
      return;
    }

    try {
      // Create configuration on backend using widget ID as string
      const response = await createConfiguration({
        board_id: boardId,
        source_type: 'livechat',
        source_main: widgetId.toString(), // Convert widget ID to string for backend
        name: widget.name,
      });

      if (response.success) {
        // Update local state
        const updatedSettings = {
          ...settings,
          chatWidgets: settings.chatWidgets.map((w) =>
            w.id === widgetId
              ? { ...w, boardId, boardName, accountId: mondayAccountId, configurationId: response.data?.id }
              : w
          ),
        };
        setSettings(updatedSettings);

        // Save to Monday storage
        await saveSettings('app_settings', updatedSettings);

        setMappingWidgetId(null);
        showNotification('Widget mapped to board successfully', 'positive');
      } else {
        showNotification(`Failed to map widget: ${response.error}`, 'negative');
      }
    } catch (error) {
      console.error('Error mapping widget:', error);
      showNotification('Failed to map widget', 'negative');
    }
  };

  /**
   * Start mapping a widget to a board
   *
   * @param {number} widgetId - ID of the widget to map
   */
  const startWidgetMapping = (widgetId: number) => {
    setMappingWidgetId(widgetId);
  };

  /**
   * Cancel widget mapping
   */
  const cancelWidgetMapping = () => {
    setMappingWidgetId(null);
  };

  /**
   * Unmap widget from board
   *
   * @param {number} widgetId - ID of the widget to unmap
   */
  const handleUnmapWidget = (widgetId: number) => {
    setSettings({
      ...settings,
      chatWidgets: settings.chatWidgets.map((w) =>
        w.id === widgetId ? { ...w, boardId: undefined, boardName: undefined } : w
      ),
    });
    showNotification('Widget unmapped from board', 'positive');
  };

  /**
   * Create a new Twilio integration and save to backend
   */
  const handleCreateIntegration = async () => {
    if (!newIntegration.accountSid || !newIntegration.authToken || !newIntegration.phoneNumber) {
      showNotification('All Twilio fields are required', 'negative');
      return;
    }

    if (!newIntegrationBoardId) {
      showNotification('Please select a board', 'negative');
      return;
    }

    if (!mondayAccountId) {
      showNotification('Monday account ID not available', 'negative');
      return;
    }

    try {
      // Determine source_type based on integration type
      const sourceType = newIntegration.type === 'sms' ? 'sms-twilio' : 'whatsapp-twilio';

      // Prepare tokens for the configuration
      const tokens = {
        provider: 'twilio',
        account_sid: newIntegration.accountSid,
        auth_token: newIntegration.authToken,
      };

      // Create configuration on backend with tokens
      const response = await createConfiguration({
        board_id: newIntegrationBoardId,
        source_type: sourceType,
        source_main: newIntegration.phoneNumber!,
        name: `${newIntegration.type!.toUpperCase()} - ${newIntegration.phoneNumber}`,
        tokens: tokens,
      });

      if (!response.success) {
        showNotification(`Failed to create integration: ${response.error}`, 'negative');
        return;
      }

      // Create integration object with backend configuration ID
      const integration: TwilioIntegration = {
        id: response.data?.id || Date.now().toString(), // Use backend config ID as integration ID
        type: newIntegration.type as 'sms' | 'whatsapp',
        provider: 'twilio',
        accountSid: newIntegration.accountSid!,
        authToken: newIntegration.authToken!,
        phoneNumber: newIntegration.phoneNumber!,
        active: newIntegration.active ?? true,
        createdAt: new Date().toISOString(),
        accountId: mondayAccountId,
        boardId: newIntegrationBoardId,
        boardName: newIntegrationBoardName,
        configurationId: response.data?.id,
      };

      // Update local state
      const updatedSettings = {
        ...settings,
        twilioIntegrations: [...settings.twilioIntegrations, integration],
      };
      setSettings(updatedSettings);

      // Save to Monday storage
      await saveSettings('app_settings', updatedSettings);

      // Reset form
      setNewIntegration({
        type: 'sms',
        provider: 'twilio',
        accountSid: '',
        authToken: '',
        phoneNumber: '',
        active: true,
      });
      setNewIntegrationBoardId('');
      setNewIntegrationBoardName('');
      setShowIntegrationForm(false);
      showNotification('Integration created and saved to backend successfully', 'positive');
    } catch (error) {
      console.error('Error creating integration:', error);
      showNotification('Failed to create integration', 'negative');
    }
  };

  /**
   * Delete a Twilio integration
   *
   * @param {string} integrationId - ID of the integration to delete
   */
  const handleDeleteIntegration = async (integrationId: string) => {
    const integration = settings.twilioIntegrations.find((i) => i.id === integrationId);
    if (!integration) return;

    try {
      // If integration has a backend configuration, delete it first
      if (integration.configurationId) {
        const response = await deleteConfiguration(integration.configurationId);

        if (!response.success) {
          showNotification(`Failed to delete configuration: ${response.error}`, 'negative');
          return;
        }
      }

      // Remove from local state
      const updatedSettings = {
        ...settings,
        twilioIntegrations: settings.twilioIntegrations.filter((i) => i.id !== integrationId),
      };
      setSettings(updatedSettings);

      // Save to Monday storage
      await saveSettings('app_settings', updatedSettings);

      showNotification('Integration deleted successfully', 'positive');
    } catch (error) {
      console.error('Error deleting integration:', error);
      showNotification('Failed to delete integration', 'negative');
    }
  };

  /**
   * Toggle integration active state
   *
   * @param {string} integrationId - ID of the integration to toggle
   */
  const handleToggleIntegration = async (integrationId: string) => {
    const integration = settings.twilioIntegrations.find((i) => i.id === integrationId);
    if (!integration) return;

    const newActiveState = !integration.active;

    try {
      // Update local state
      const updatedSettings = {
        ...settings,
        twilioIntegrations: settings.twilioIntegrations.map((i) =>
          i.id === integrationId ? { ...i, active: newActiveState } : i
        ),
      };
      setSettings(updatedSettings);

      // Save to Monday storage
      await saveSettings('app_settings', updatedSettings);

      // If integration has a backend configuration, update it
      if (integration.configurationId) {
        const response = await updateConfiguration(integration.configurationId, {
          source_type: integration.type === 'sms' ? 'sms-twilio' : 'whatsapp-twilio',
          source_main: integration.phoneNumber,
          board_id: integration.boardId || '',
        });

        if (!response.success) {
          console.error('Failed to update backend configuration:', response.error);
          showNotification('Integration toggled locally but backend sync failed', 'negative');
        }
      }
    } catch (error) {
      console.error('Error toggling integration:', error);
      showNotification('Failed to toggle integration', 'negative');
    }
  };

  /**
   * Update integration board mapping and update backend configuration
   *
   * @param {string} integrationId - ID of the integration to update
   * @param {string} boardId - Monday.com board ID
   * @param {string} boardName - Monday.com board name
   */
  const handleMapIntegration = async (integrationId: string, boardId: string, boardName: string) => {
    const integration = settings.twilioIntegrations.find((i) => i.id === integrationId);
    if (!integration) return;

    if (!mondayAccountId) {
      showNotification('Monday account ID not available', 'negative');
      return;
    }

    try {
      // Determine source_type based on integration type
      const sourceType = integration.type === 'sms' ? 'sms-twilio' : 'whatsapp-twilio';

      // Prepare tokens for the configuration
      const tokens = {
        provider: integration.provider,
        account_sid: integration.accountSid,
        auth_token: integration.authToken,
      };

      let response;

      // Check if integration already has a configuration (should always have one now)
      if (integration.configurationId) {
        // Update existing configuration with new board
        response = await updateConfiguration(integration.configurationId, {
          board_id: boardId,
          source_type: sourceType,
          source_main: integration.phoneNumber,
          name: `${integration.type.toUpperCase()} - ${integration.phoneNumber}`,
          tokens: tokens,
        });
      } else {
        // Fallback: Create new configuration if somehow missing
        response = await createConfiguration({
          board_id: boardId,
          source_type: sourceType,
          source_main: integration.phoneNumber,
          name: `${integration.type.toUpperCase()} - ${integration.phoneNumber}`,
          tokens: tokens,
        });
      }

      if (response.success) {
        // Update local state
        const updatedSettings = {
          ...settings,
          twilioIntegrations: settings.twilioIntegrations.map((i) =>
            i.id === integrationId
              ? { ...i, boardId, boardName, accountId: mondayAccountId, configurationId: response.data?.id || integration.configurationId }
              : i
          ),
        };
        setSettings(updatedSettings);

        // Save to Monday storage
        await saveSettings('app_settings', updatedSettings);

        setMappingIntegrationId(null);
        showNotification('Integration remapped to board successfully', 'positive');
      } else {
        showNotification(`Failed to remap integration: ${response.error}`, 'negative');
      }
    } catch (error) {
      console.error('Error remapping integration:', error);
      showNotification('Failed to remap integration', 'negative');
    }
  };

  /**
   * Start mapping an integration to a board
   *
   * @param {string} integrationId - ID of the integration to map
   */
  const startIntegrationMapping = (integrationId: string) => {
    setMappingIntegrationId(integrationId);
  };

  /**
   * Cancel integration mapping
   */
  const cancelIntegrationMapping = () => {
    setMappingIntegrationId(null);
  };

  /**
   * Generate HTML embed code for a chat widget
   *
   * @param {ChatWidget} widget - The widget to generate embed code for
   * @returns {string} The HTML embed code
   */
  const generateEmbedCode = (widget: ChatWidget): string => {
    const orgId = mondayAccountId;
    const configId = widget.configurationId || widget.id;

    return `<script>
    window.SUPPORT_UNICORN_CONFIG = {
        orgId: '${orgId}',
        configurationId: '${configId}',
        theme: {
            primaryColor: '#3b82f6',       // Bubble and header background
            backgroundColor: '#ffffff',    // Chat popup background
            textColor: '#ffffff',          // Header text color
            userMessageColor: '#3b82f6',   // User message bubble color
            botMessageColor: '#f3f4f6'     // Bot message bubble color
        },
        icon: {
            type: 'emoji',                 // 'emoji' or 'svg'
            value: '🦄',                   // Emoji or custom SVG string
            size: 'w-10 h-10'              // Icon size (Tailwind classes)
        },
        title: 'Support Chat',           // Header title
        placeholder: 'Type your message...', // Input placeholder
    };
</script>
<script src="https://cdn.supportunicorn.com/v3/chat-widget.min.js"></script>`;
  };

  return (
    <div className="settings-page">
      <Box padding={Box.paddings.LARGE}>
        <Flex justify={Flex.justify.SPACE_BETWEEN} align={Flex.align.CENTER}>
          <div>
            <Heading type={Heading.types.H1}>App Settings</Heading>
            <p className="settings-description">
              Configure your Support Unicorn integration settings
            </p>
          </div>
          <Button
            onClick={loadConfigurationsFromBackend}
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.MEDIUM}
          >
            🔄 Sync from Backend
          </Button>
        </Flex>

        {/* Chat Widgets Management Section */}
        <section className="settings-section">
          <Flex justify={Flex.justify.SPACE_BETWEEN} align={Flex.align.CENTER}>
            <Heading type={Heading.types.H2}>Chat Widgets Management</Heading>
            <Button
              onClick={() => setShowWidgetForm(!showWidgetForm)}
              kind={Button.kinds.PRIMARY}
              size={Button.sizes.SMALL}
            >
              {showWidgetForm ? 'Cancel' : '+ Create Widget'}
            </Button>
          </Flex>

          {/* Create Widget Form */}
          {showWidgetForm && (
            <Box marginTop={Box.marginTops.MEDIUM} className="widget-form">
              <Flex direction={Flex.directions.COLUMN} gap={Flex.gaps.SMALL}>
                <TextField
                  title="Widget Name"
                  placeholder="Enter widget name"
                  value={newWidgetName}
                  onChange={setNewWidgetName}
                  size={TextField.sizes.MEDIUM}
                />
                <Button
                  onClick={handleCreateWidget}
                  kind={Button.kinds.PRIMARY}
                  size={Button.sizes.SMALL}
                >
                  Create
                </Button>
              </Flex>
            </Box>
          )}

          {/* Widgets List */}
          <Box marginTop={Box.marginTops.MEDIUM}>
            {settings.chatWidgets.length === 0 ? (
              <p className="empty-state">
                No chat widgets created yet. Click 'Create Widget' to get started!
              </p>
            ) : (
              <Flex direction={Flex.directions.COLUMN} gap={Flex.gaps.MEDIUM}>
                {settings.chatWidgets.map((widget) => (
                  <Box key={widget.id} className="widget-card">
                    <Flex direction={Flex.directions.COLUMN} gap={Flex.gaps.MEDIUM}>
                      {/* Header Row */}
                      <div className="widget-header-row">
                        <Heading type={Heading.types.H3} className="widget-name">
                          {widget.name}
                        </Heading>
                        <div className="widget-toggle-container">
                          <span className="toggle-label-inline">
                            {widget.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <Toggle
                            isSelected={widget.enabled}
                            onChange={() => handleToggleWidget(widget.id)}
                          />
                        </div>
                      </div>

                      {/* Info Row */}
                      <Flex direction={Flex.directions.COLUMN} gap={Flex.gaps.SMALL} className="widget-info">
                        <div className="info-row">
                          <span className="info-label">Status:</span>
                          <span className={`status-badge ${widget.enabled ? 'status-enabled' : 'status-disabled'}`}>
                            {widget.enabled ? '🟢 Enabled' : '🔴 Disabled'}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Board:</span>
                          <span className={widget.boardName ? 'board-mapped' : 'board-unmapped'}>
                            {widget.boardName || '❌ Not mapped'}
                          </span>
                        </div>
                      </Flex>

                      {/* Board Mapping Dropdown */}
                      {mappingWidgetId === widget.id && (
                        <Box marginTop={Box.marginTops.SMALL} className="mapping-section">
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
                              Select Board to Map
                            </label>
                            <Dropdown
                              placeholder="Select board"
                              options={boards.map((board) => ({
                                value: board.id,
                                label: board.name,
                              }))}
                              onChange={(option: { value: string; label: string }) => {
                                handleMapWidget(widget.id, option.value, option.label);
                              }}
                              size={Dropdown.sizes.LARGE}
                              disabled={boardsLoading}
                              className="board-dropdown"
                            />
                          </div>
                          <Flex gap={Flex.gaps.SMALL}>
                            <Button
                              onClick={cancelWidgetMapping}
                              kind={Button.kinds.TERTIARY}
                              size={Button.sizes.MEDIUM}
                            >
                              Cancel
                            </Button>
                          </Flex>
                        </Box>
                      )}

                      {/* Action Buttons */}
                      {mappingWidgetId !== widget.id && (
                        <Flex gap={Flex.gaps.SMALL} wrap={true}>
                          <Button
                            onClick={() => startWidgetMapping(widget.id)}
                            kind={Button.kinds.SECONDARY}
                            size={Button.sizes.SMALL}
                            disabled={boardsLoading}
                          >
                            {widget.boardId ? '🔗 Remap Board' : '🔗 Map to Board'}
                          </Button>
                          {widget.boardId && (
                            <Button
                              onClick={() => handleUnmapWidget(widget.id)}
                              kind={Button.kinds.TERTIARY}
                              size={Button.sizes.SMALL}
                            >
                              Unmap
                            </Button>
                          )}
                          <Button
                            onClick={() => setShowInstructionsWidgetId(widget.id)}
                            kind={Button.kinds.SECONDARY}
                            size={Button.sizes.SMALL}
                          >
                            📋 Instructions
                          </Button>
                          <Button
                            onClick={() => {
                              if (confirm(`Delete widget "${widget.name}"?`)) {
                                handleDeleteWidget(widget.id);
                              }
                            }}
                            kind={Button.kinds.TERTIARY}
                            size={Button.sizes.SMALL}
                          >
                            🗑️ Delete
                          </Button>
                        </Flex>
                      )}
                    </Flex>
                  </Box>
                ))}
              </Flex>
            )}
          </Box>

          {/* Instructions Modal */}
          {showInstructionsWidgetId !== null && (() => {
            const widget = settings.chatWidgets.find((w) => w.id === showInstructionsWidgetId);
            if (!widget) return null;

            const embedCode = generateEmbedCode(widget);

            return (
              <div className="modal-overlay" onClick={() => setShowInstructionsWidgetId(null)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-inner">
                    {/* Modal Header */}
                    <div className="modal-header">
                      <h2 className="modal-title">🦄 Widget Instructions</h2>
                      <Button
                        onClick={() => setShowInstructionsWidgetId(null)}
                        kind={Button.kinds.TERTIARY}
                        size={Button.sizes.SMALL}
                      >
                        ✕ Close
                      </Button>
                    </div>

                    <div className="modal-divider"></div>

                    {/* Widget Details */}
                    <div className="modal-section">
                      <h3 className="section-title">Widget Details</h3>
                      <div className="widget-details">
                        <div className="detail-row">
                          <span className="detail-label">Widget:</span>
                          <span className="detail-value">{widget.name}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Status:</span>
                          <span className={`status-badge ${widget.enabled ? 'status-enabled' : 'status-disabled'}`}>
                            {widget.enabled ? '🟢 Enabled' : '🔴 Disabled'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Widget ID:</span>
                          <span className="detail-value">{widget.id}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Organization ID:</span>
                          <span className="detail-value">{mondayAccountId}</span>
                        </div>
                      </div>
                    </div>

                    <div className="modal-divider"></div>

                    {/* Embed Code */}
                    <div className="modal-section">
                      <h3 className="section-title">HTML Embed Code</h3>
                      <p className="section-description">
                        Copy this code and paste it into your website before the closing <code>&lt;/body&gt;</code> tag:
                      </p>
                      <div className="code-container">
                        <pre className="code-block">{embedCode}</pre>
                      </div>
                      <div className="button-container">
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(embedCode);
                            showNotification('Embed code copied to clipboard!', 'positive');
                          }}
                          kind={Button.kinds.SECONDARY}
                          size={Button.sizes.MEDIUM}
                        >
                          📋 Copy Code
                        </Button>
                      </div>
                    </div>

                    <div className="modal-divider"></div>

                    {/* Setup Instructions */}
                    <div className="modal-section">
                      <h3 className="section-title">Setup Instructions</h3>
                      <ol className="instructions-list">
                        <li>Copy the HTML code above</li>
                        <li>Paste it into your website before the closing <code>&lt;/body&gt;</code> tag</li>
                        <li>Customize colors, icon, title, and placeholder as needed</li>
                        <li>The widget will automatically connect to your Support Unicorn backend</li>
                      </ol>
                    </div>

                    <div className="modal-divider"></div>

                    {/* Tips */}
                    <div className="modal-section">
                      <div className="tip-box">
                        <p className="tip-text">
                          <strong>💡 Tip:</strong> You can modify the theme colors, icon (emoji or SVG), title, and placeholder text in the <code>SUPPORT_UNICORN_CONFIG</code> object to match your brand.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </section>

        <Divider className="section-divider" />

        {/* Twilio Integrations Section */}
        <section className="settings-section">
          <Flex justify={Flex.justify.SPACE_BETWEEN} align={Flex.align.CENTER}>
            <Heading type={Heading.types.H2}>Twilio Integrations (SMS & WhatsApp)</Heading>
            <Button
              onClick={() => setShowIntegrationForm(!showIntegrationForm)}
              kind={Button.kinds.PRIMARY}
              size={Button.sizes.SMALL}
            >
              {showIntegrationForm ? 'Cancel' : '+ Add Integration'}
            </Button>
          </Flex>

          {/* Create Integration Form */}
          {showIntegrationForm && (
            <Box marginTop={Box.marginTops.MEDIUM} className="integration-form">
              <Flex direction={Flex.directions.COLUMN} gap={Flex.gaps.SMALL}>
                <Flex gap={Flex.gaps.SMALL} align={Flex.align.CENTER}>
                  <span>Type:</span>
                  <Button
                    onClick={() => setNewIntegration({ ...newIntegration, type: 'sms' })}
                    kind={newIntegration.type === 'sms' ? Button.kinds.PRIMARY : Button.kinds.TERTIARY}
                    size={Button.sizes.SMALL}
                  >
                    📱 SMS
                  </Button>
                  <Button
                    onClick={() => setNewIntegration({ ...newIntegration, type: 'whatsapp' })}
                    kind={newIntegration.type === 'whatsapp' ? Button.kinds.PRIMARY : Button.kinds.TERTIARY}
                    size={Button.sizes.SMALL}
                  >
                    💚 WhatsApp
                  </Button>
                </Flex>

                <TextField
                  title="Twilio Account SID"
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={newIntegration.accountSid || ''}
                  onChange={(value: string) =>
                    setNewIntegration({ ...newIntegration, accountSid: value })
                  }
                  size={TextField.sizes.MEDIUM}
                />

                <TextField
                  title="Twilio Auth Token"
                  placeholder="Enter your Twilio Auth Token"
                  value={newIntegration.authToken || ''}
                  onChange={(value: string) =>
                    setNewIntegration({ ...newIntegration, authToken: value })
                  }
                  size={TextField.sizes.MEDIUM}
                  type={TextField.types.PASSWORD}
                />

                <TextField
                  title="Twilio Phone Number"
                  placeholder="+1234567890"
                  value={newIntegration.phoneNumber || ''}
                  onChange={(value: string) =>
                    setNewIntegration({ ...newIntegration, phoneNumber: value })
                  }
                  size={TextField.sizes.MEDIUM}
                />

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
                    Select Board *
                  </label>
                  <Dropdown
                    placeholder="Select board to receive messages"
                    options={boards.map((board) => ({
                      value: board.id,
                      label: board.name,
                    }))}
                    onChange={(option: { value: string; label: string }) => {
                      setNewIntegrationBoardId(option.value);
                      setNewIntegrationBoardName(option.label);
                    }}
                    size={Dropdown.sizes.LARGE}
                    disabled={boardsLoading}
                    className="board-dropdown"
                  />
                </div>

                <Button
                  onClick={handleCreateIntegration}
                  kind={Button.kinds.PRIMARY}
                  size={Button.sizes.SMALL}
                >
                  Create Integration
                </Button>
              </Flex>
            </Box>
          )}

          {/* Integrations List */}
          <Box marginTop={Box.marginTops.MEDIUM}>
            {settings.twilioIntegrations.length === 0 ? (
              <p className="empty-state">
                No integrations configured yet. Click 'Add Integration' to connect SMS or WhatsApp!
              </p>
            ) : (
              <Flex direction={Flex.directions.COLUMN} gap={Flex.gaps.MEDIUM}>
                {settings.twilioIntegrations.map((integration) => (
                  <Box key={integration.id} className="integration-card">
                    <Flex direction={Flex.directions.COLUMN} gap={Flex.gaps.MEDIUM}>
                      {/* Header Row */}
                      <div className="widget-header-row">
                        <Heading type={Heading.types.H3} className="widget-name">
                          {integration.type === 'sms' ? '📱 SMS Integration' : '💚 WhatsApp Integration'}
                        </Heading>
                        <div className="widget-toggle-container">
                          <span className="toggle-label-inline">
                            {integration.active ? 'Active' : 'Inactive'}
                          </span>
                          <Toggle
                            isSelected={integration.active}
                            onChange={() => handleToggleIntegration(integration.id)}
                          />
                        </div>
                      </div>

                      {/* Info Row */}
                      <Flex direction={Flex.directions.COLUMN} gap={Flex.gaps.SMALL} className="widget-info">
                        <div className="info-row">
                          <span className="info-label">Type:</span>
                          <span>{integration.type.toUpperCase()}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Provider:</span>
                          <span>{integration.provider.charAt(0).toUpperCase() + integration.provider.slice(1)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Phone:</span>
                          <span>{integration.phoneNumber}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Board:</span>
                          <span className={integration.boardName ? 'board-mapped' : 'board-unmapped'}>
                            {integration.boardName || '❌ Not configured'}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Status:</span>
                          <span className={`status-badge ${integration.active ? 'status-enabled' : 'status-disabled'}`}>
                            {integration.active ? '🟢 Active' : '🔴 Inactive'}
                          </span>
                        </div>
                      </Flex>

                      {/* Board Mapping Dropdown */}
                      {mappingIntegrationId === integration.id && (
                        <Box marginTop={Box.marginTops.SMALL} className="mapping-section">
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
                              Select Board to Map
                            </label>
                            <Dropdown
                              placeholder="Select board"
                              options={boards.map((board) => ({
                                value: board.id,
                                label: board.name,
                              }))}
                              onChange={(option: { value: string; label: string }) => {
                                handleMapIntegration(integration.id, option.value, option.label);
                              }}
                              size={Dropdown.sizes.LARGE}
                              disabled={boardsLoading}
                              className="board-dropdown"
                            />
                          </div>
                          <Flex gap={Flex.gaps.SMALL}>
                            <Button
                              onClick={cancelIntegrationMapping}
                              kind={Button.kinds.TERTIARY}
                              size={Button.sizes.MEDIUM}
                            >
                              Cancel
                            </Button>
                          </Flex>
                        </Box>
                      )}

                      {/* Action Buttons */}
                      {mappingIntegrationId !== integration.id && (
                        <Flex gap={Flex.gaps.SMALL} wrap={true}>
                          <Button
                            onClick={() => startIntegrationMapping(integration.id)}
                            kind={Button.kinds.SECONDARY}
                            size={Button.sizes.SMALL}
                            disabled={boardsLoading}
                          >
                            {integration.boardId ? '🔗 Remap Board' : '🔗 Map to Board'}
                          </Button>
                          <Button
                            onClick={() => {
                              if (confirm(`Delete ${integration.type} integration for ${integration.phoneNumber}?`)) {
                                handleDeleteIntegration(integration.id);
                              }
                            }}
                            kind={Button.kinds.TERTIARY}
                            size={Button.sizes.SMALL}
                          >
                            🗑️ Delete
                          </Button>
                        </Flex>
                      )}
                    </Flex>
                  </Box>
                ))}
              </Flex>
            )}
          </Box>
        </section>
      </Box>

      {/* Toast Notification */}
      {showToast && (
        <Toast
          open={showToast}
          type={toastType === 'positive' ? Toast.types.POSITIVE : Toast.types.NEGATIVE}
          onClose={() => setShowToast(false)}
          autoHideDuration={3000}
        >
          {toastMessage}
        </Toast>
      )}
    </div>
  );
};

export default SettingsPage;
